import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Trophy, ArrowRight, Target, TrendingUp, TrendingDown, Minus, Sparkles, Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageContainer } from '../../components/shared/PageContainer';
import { PageHero, HeroStatPill } from '../../components/shared/PageHero';
import { itemVariants } from '../../components/shared/animations';
import { Skeleton } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { useDjaySelection } from '../../mascot';
import { GroupId } from '../../config/systemConfig';
import { error as logError } from '../../utils/logger';
import { AssessmentConfig, AssessmentDomainId, AssessmentHistoryEntry, AssessmentReport, SubTestDefinition } from '../types';
import { getAssessmentHistory, checkDomainCooldown, getReport } from '../utils/assessmentStorage';
import { getAvailableSubTests, DOMAIN_META, DOMAIN_MODULE_MAP } from '../utils/assessmentDomainConfig';
import { dataLayer } from '../../data/dataLayer';
interface CooldownState {
  canStart: boolean;
  remainingMs: number;
  lastCompletedAt: number;
}
interface AssessmentPageProps {
  selectedGroup: GroupId;
  onStart: (config: AssessmentConfig) => void;
  onViewReport: (report: AssessmentReport) => void;
  learnerId?: string;
}
export function AssessmentPage({
  selectedGroup,
  onStart,
  onViewReport,
  learnerId
}: AssessmentPageProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [reports, setReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAllResults, setShowAllResults] = useState(false);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [domainCooldowns, setDomainCooldowns] = useState<Record<AssessmentDomainId, CooldownState>>({
    rekenen: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    taalverzorging: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    'begrijpend-lezen': {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    }
  });
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setIsLoading(true);
      try {
        const h = await getAssessmentHistory(selectedGroup);
        if (isMounted) setHistory(h);
        const loadedReports: Record<string, AssessmentReport> = {};
        await Promise.all(h.map(async entry => {
          const report = await getReport(entry.attemptId);
          if (report) {
            loadedReports[entry.attemptId] = report;
          }
        }));
        if (isMounted) setReports(loadedReports);
        if (learnerId) {
          const assignmentsData = await dataLayer.getTeacherAssignments();
          const allAssignments = Object.values(assignmentsData);
          const myAssignments = allAssignments.filter((a: any) => {
            if (a.status !== 'active') return false;
            if (a.deadline && Date.now() > a.deadline) return false;
            const completedBy = a.completedByStudentIds as string[] || [];
            if (completedBy.includes(learnerId!)) return false;
            if (a.wholeClass && a.group === selectedGroup) return true;
            if (a.targetStudentIds && a.targetStudentIds.includes(learnerId)) return true;
            return false;
          });
          if (isMounted) setAssignments(myAssignments.sort((a: any, b: any) => b.createdAt - a.createdAt));
        }
      } catch (e) {
        logError('Failed to load assessment history', e);
      }
      try {
        const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
        const cooldowns: Record<string, CooldownState> = {};
        await Promise.all(domains.map(async domainId => {
          cooldowns[domainId] = await checkDomainCooldown(selectedGroup, domainId);
        }));
        if (isMounted) setDomainCooldowns(cooldowns as Record<AssessmentDomainId, CooldownState>);
      } catch (e) {
        logError('Failed to check cooldowns', e);
      }
      if (isMounted) setIsLoading(false);
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [selectedGroup, learnerId]);
  const handleStartSubTest = (subTest: SubTestDefinition) => {
    const config: AssessmentConfig = {
      questionCount: subTest.questionCount,
      moduleFilter: subTest.modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now(),
      domainId: subTest.domainId
    };
    onStart(config);
  };
  const handleStartAssignment = (assignment: any) => {
    const modules = DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null;
    const config: AssessmentConfig = {
      domainId: assignment.domainId,
      mode: assignment.type === 'targeted' ? 'targeted' : 'general',
      targetFamilyIds: assignment.selectedFamilyIds || [],
      assignmentId: assignment.id,
      questionCount: assignment.questionCount || 30,
      moduleFilter: modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now()
    };
    onStart(config);
  };
  const hasHistory = history.length > 0;
  const bestScore = hasHistory ? Math.max(...history.map(h => h.score)) : null;
  const availableSubTests = getAvailableSubTests(selectedGroup);
  const displayedHistory = showAllResults ? history : history.slice(0, 3);
  const anyDomainOnCooldown = Object.values(domainCooldowns).some(c => !c.canStart);
  const maxRemainingMs = Math.max(...Object.values(domainCooldowns).map(c => c.remainingMs));
  return <PageContainer data-id="element-819">
      <motion.div variants={itemVariants} data-id="element-820">
        <PageHero title="Toetsen" subtitle={hasHistory ? 'Klaar om te laten zien hoeveel je gegroeid bent?' : 'Klaar voor je eerste toets? Ontdek waar je staat!'} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={hasHistory ? 'excited' : 'encouraging'} djayVariant={selectedVariant} icon={ClipboardCheck} iconBgClass="bg-orange-400" floatingEmojis={['📝', '⭐']} data-id="element-821">
          <HeroStatPill icon={Target} data-id="element-822">Groep {selectedGroup}</HeroStatPill>
          {hasHistory && <HeroStatPill icon={ClipboardCheck} data-id="element-823">
              {history.length} toets{history.length !== 1 ? 'en' : ''} gemaakt
            </HeroStatPill>}
          {bestScore !== null && <HeroStatPill icon={Trophy} data-id="element-824">Topscore: {bestScore}%</HeroStatPill>}
        </PageHero>
      </motion.div>

      {/* Teacher-Assigned Assessments Section */}
      {assignments.length > 0 && <motion.div variants={itemVariants} className="space-y-4" data-id="element-825">
          <div className="flex items-center gap-3" data-id="element-826">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center" data-id="element-827">
              <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-828" />
            </div>
            <div data-id="element-829">
              <h2 className="text-2xl font-black text-slate-900" data-id="element-830">
                Van je Leerkracht
              </h2>
              <p className="text-sm text-slate-600 font-medium" data-id="element-831">
                Deze toetsen zijn speciaal voor jou klaargezet
              </p>
            </div>
          </div>

          <div className="space-y-3" data-id="element-832">
            {assignments.map(assignment => {
          const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
          const Icon = meta?.icon;
          const hasDeadline = assignment.deadline;
          const isUrgent = hasDeadline && assignment.deadline - Date.now() < 2 * 24 * 60 * 60 * 1000;
          return <Card key={assignment.id} className={`p-5 border-2 transition-all hover:shadow-md ${isUrgent ? 'border-orange-300 bg-orange-50/50' : 'border-indigo-200 bg-indigo-50/30'}`} data-id="element-833">
                  <div className="flex items-start gap-4" data-id="element-834">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-835">
                      {Icon ? <Icon className="w-7 h-7" data-id="element-836" /> : '📝'}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-837">
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-838">
                        <h3 className="font-black text-lg text-slate-900" data-id="element-839">
                          {assignment.domainLabel}
                        </h3>
                        {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-200 text-xs" data-id="element-840">
                            Gericht
                          </Badge>}
                        {isUrgent && <Badge variant="default" className="bg-orange-100 text-orange-700 border-orange-300 text-xs animate-pulse" data-id="element-841">
                            Bijna deadline!
                          </Badge>}
                      </div>

                      {assignment.type === 'targeted' && assignment.selectedSkillSummary && <p className="text-sm text-slate-600 mb-3 font-medium" data-id="element-842">
                            {assignment.selectedSkillSummary}
                          </p>}

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4 flex-wrap" data-id="element-843">
                        <div className="flex items-center gap-1.5" data-id="element-844">
                          <Clock className="w-4 h-4" data-id="element-845" />
                          <span className="font-medium" data-id="element-846">~15 min</span>
                        </div>
                        {hasDeadline && <div className={`flex items-center gap-1.5 ${isUrgent ? 'text-orange-600 font-bold' : 'text-slate-600'}`} data-id="element-847">
                            <Calendar className="w-4 h-4" data-id="element-848" />
                            <span data-id="element-849">
                              Deadline:{' '}
                              {new Date(assignment.deadline).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short'
                      })}
                            </span>
                          </div>}
                      </div>

                      <Button onClick={() => handleStartAssignment(assignment)} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-850">
                        Start Toets <ArrowRight className="w-4 h-4 ml-2" data-id="element-851" />
                      </Button>
                    </div>
                  </div>
                </Card>;
        })}
          </div>

          <div className="bg-indigo-50 border-2 border-indigo-100 rounded-xl p-4 flex items-start gap-3" data-id="element-852">
            <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} data-id="element-853" />
            <div className="flex-1" data-id="element-854">
              <p className="text-sm text-indigo-900 font-medium leading-relaxed" data-id="element-855">
                <strong data-id="element-856">Tip:</strong> Deze toetsen zijn door je leerkracht
                klaargezet. Doe rustig je best en lees elke vraag goed!
              </p>
            </div>
          </div>
        </motion.div>}

      {/* Niveaubepaling Section */}
      <motion.div variants={itemVariants} className="space-y-4" data-id="element-857">
        <div className="flex items-center gap-3" data-id="element-858">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center" data-id="element-859">
            <Target className="w-5 h-5 text-orange-600" data-id="element-860" />
          </div>
          <div data-id="element-861">
            <h2 className="text-2xl font-black text-slate-900" data-id="element-862">
              Ontdek je Niveau
            </h2>
            <p className="text-sm text-slate-600 font-medium" data-id="element-863">
              Vrijwillige toetsen om te zien waar je staat
            </p>
          </div>
        </div>

        {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-864">
            {[1, 2, 3].map(i => <Card key={i} className="p-4" data-id="element-865">
                <Skeleton className="w-10 h-10 rounded-xl mb-3" data-id="element-866" />
                <Skeleton className="h-5 w-3/4 mb-2" data-id="element-867" />
                <Skeleton className="h-4 w-full mb-4" data-id="element-868" />
                <Skeleton className="h-4 w-20 mb-2" data-id="element-869" />
                <Skeleton className="h-4 w-24" data-id="element-870" />
              </Card>)}
          </div> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-871">
            {availableSubTests.map((subTest, i) => {
          const meta = DOMAIN_META[subTest.domainId];
          const cooldown = domainCooldowns[subTest.domainId];
          const canStartThis = cooldown?.canStart !== false && !isLoading;
          return <motion.div key={subTest.domainId} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="h-full" data-id="element-872">
                  <button onClick={() => canStartThis && handleStartSubTest(subTest)} disabled={!canStartThis} className={`w-full text-left bg-white rounded-2xl p-5 border-2 shadow-sm transition-all flex flex-col h-full ${!canStartThis ? 'opacity-60 cursor-not-allowed border-slate-200' : `hover:shadow-lg hover:-translate-y-1 ${meta.borderColor}`}`} data-id="element-873">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${meta.bgColor} ${meta.color}`} data-id="element-874">
                      <meta.icon className="w-6 h-6" data-id="element-875" />
                    </div>

                    <h3 className={`font-black text-lg mb-1 ${meta.color}`} data-id="element-876">
                      {subTest.label}
                    </h3>
                    <p className="text-sm text-slate-600 font-medium mb-4 flex-grow leading-snug" data-id="element-877">
                      {meta.description}
                    </p>

                    <div className="space-y-2 mt-auto" data-id="element-878">
                      <div className="flex items-center text-sm text-slate-500 font-medium" data-id="element-879">
                        <ClipboardCheck className="w-4 h-4 mr-1.5" data-id="element-880" />
                        {subTest.questionCount} vragen
                      </div>
                      <div className="flex items-center text-sm text-slate-500 font-medium" data-id="element-881">
                        <Clock className="w-4 h-4 mr-1.5" data-id="element-882" />~
                        {subTest.estimatedMinutes} minuten
                      </div>

                      {subTest.shortNote && <div className={`mt-2 text-xs font-bold px-2 py-1 rounded-md inline-block ${meta.bgColor} ${meta.color}`} data-id="element-883">
                          {subTest.shortNote}
                        </div>}

                      {cooldown && !cooldown.canStart && <div className="mt-2 text-xs font-bold px-2 py-1 rounded-md inline-block bg-slate-100 text-slate-500" data-id="element-884">
                          ⏳ Nog {formatRemainingTime(cooldown.remainingMs)}
                        </div>}
                    </div>
                  </button>
                </motion.div>;
        })}
          </div>}

        <div className="bg-amber-50 border-2 border-amber-100 rounded-xl p-4 flex items-start gap-3" data-id="element-885">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-amber-100" data-id="element-886">
            💡
          </div>
          <div className="flex-1" data-id="element-887">
            <h3 className="font-bold text-amber-900 mb-1" data-id="element-888">Goed om te weten</h3>
            <p className="text-sm text-amber-800 font-medium leading-relaxed" data-id="element-889">
              Deze toetsen laten zien waar je staat en tellen niet mee voor je
              oefen-voortgang. Doe rustig je best en lees elke vraag goed!
            </p>
          </div>
        </div>

        {anyDomainOnCooldown && <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4 flex items-start gap-3" data-id="element-890">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-slate-200" data-id="element-891">
              ⏳
            </div>
            <div className="flex-1" data-id="element-892">
              <h3 className="font-bold text-slate-700 mb-1" data-id="element-893">Even wachten</h3>
              <p className="text-sm text-slate-600 font-medium leading-relaxed" data-id="element-894">
                Je hebt recent een toets gemaakt. Sommige toetsen zijn nog niet
                beschikbaar. Wacht nog{' '}
                <strong data-id="element-895">{formatRemainingTime(maxRemainingMs)}</strong> voor alle
                toetsen.
              </p>
            </div>
          </div>}
      </motion.div>

      {/* Results History Section */}
      <motion.div variants={itemVariants} className="space-y-4" data-id="element-896">
        <div className="flex items-center gap-3" data-id="element-897">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center" data-id="element-898">
            <Trophy className="w-5 h-5 text-amber-600" data-id="element-899" />
          </div>
          <div data-id="element-900">
            <h2 className="text-2xl font-black text-slate-900" data-id="element-901">
              Jouw Resultaten
            </h2>
            <p className="text-sm text-slate-600 font-medium" data-id="element-902">
              Bekijk je eerdere toetsresultaten
            </p>
          </div>
        </div>

        {isLoading ? <div className="space-y-3" data-id="element-903">
            {[1, 2, 3].map(i => <Card key={i} className="p-4" data-id="element-904">
                <div className="flex items-center gap-3" data-id="element-905">
                  <Skeleton className="w-10 h-10 rounded-xl" data-id="element-906" />
                  <div className="flex-1" data-id="element-907">
                    <Skeleton className="h-4 w-32 mb-2" data-id="element-908" />
                    <Skeleton className="h-3 w-48" data-id="element-909" />
                  </div>
                  <Skeleton className="h-9 w-32" data-id="element-910" />
                </div>
              </Card>)}
          </div> : hasHistory ? <div className="space-y-3" data-id="element-911">
            {displayedHistory.map((entry, index) => {
          const date = new Date(entry.completedAt);
          const dateStr = date.toLocaleDateString('nl-NL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          const scoreColor = entry.score >= 70 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : entry.score >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-rose-700 bg-rose-50 border-rose-200';
          const report = reports[entry.attemptId];
          const prevEntry = history.slice(index + 1).find(h => h.domainId === entry.domainId);
          let TrendIcon = Minus;
          let trendColor = 'text-slate-400';
          if (prevEntry) {
            if (entry.score > prevEntry.score) {
              TrendIcon = TrendingUp;
              trendColor = 'text-emerald-500';
            } else if (entry.score < prevEntry.score) {
              TrendIcon = TrendingDown;
              trendColor = 'text-rose-500';
            }
          }
          const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
          const isAssigned = !!entry.assignmentId;
          return <Card key={entry.attemptId} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:shadow-md transition-shadow" data-id="element-912">
                  <div className="flex items-center gap-3" data-id="element-913">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${scoreColor}`} data-id="element-914">
                      <span className="font-black text-lg" data-id="element-915">{entry.score}%</span>
                    </div>
                    <div data-id="element-916">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-917">
                        <h3 className="font-bold text-sm text-slate-900" data-id="element-918">
                          {domainMeta ? domainMeta.shortLabel : 'Toets'}
                        </h3>
                        {domainMeta && <span className={`flex items-center justify-center w-5 h-5 rounded ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-919">
                            <domainMeta.icon className="w-3 h-3" data-id="element-920" />
                          </span>}
                        {prevEntry && <TrendIcon className={`w-4 h-4 ${trendColor}`} data-id="element-921" />}
                        {isAssigned ? <Badge variant="default" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs" data-id="element-922">
                            Van leerkracht
                          </Badge> : <Badge variant="default" className="bg-orange-100 text-orange-700 border-orange-200 text-xs" data-id="element-923">
                            Niveaubepaling
                          </Badge>}
                      </div>
                      <p className="text-xs text-slate-500 font-medium flex items-center gap-2 mt-1" data-id="element-924">
                        <span className="flex items-center gap-1" data-id="element-925">
                          <ClipboardCheck className="w-3 h-3" data-id="element-926" />
                          {entry.totalQuestions} vragen
                        </span>
                        <span className="text-slate-300" data-id="element-927">·</span>
                        <span data-id="element-928">{dateStr}</span>
                      </p>
                    </div>
                  </div>

                  {report && !isAssigned && <Button onClick={() => onViewReport(report)} variant="secondary" size="sm" className="bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200 w-full sm:w-auto" data-id="element-929">
                      Bekijk Rapport{' '}
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" data-id="element-930" />
                    </Button>}
                  {isAssigned && <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium" data-id="element-931">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-932" />
                      Afgerond
                    </div>}
                </Card>;
        })}

            {history.length > 3 && <Button variant="secondary" className="w-full bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200" onClick={() => setShowAllResults(!showAllResults)} data-id="element-933">
                {showAllResults ? 'Toon minder' : `Toon alle resultaten (${history.length})`}
              </Button>}
          </div> : <Card className="p-8 text-center border-2 border-amber-100 bg-amber-50/30" data-id="element-934">
            <div className="text-5xl mb-3" data-id="element-935">🌟</div>
            <p className="text-slate-900 font-bold text-lg mb-1" data-id="element-936">
              Na je eerste toets zie je hier je resultaten!
            </p>
            <p className="text-slate-600 font-medium" data-id="element-937">
              Start hierboven een toets om te zien waar je staat.
            </p>
          </Card>}
      </motion.div>
    </PageContainer>;
}
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes <= 1) return 'minder dan een minuut';
  if (minutes < 60) return `${minutes} minuten`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} uur`;
  return `${hours} uur en ${remainingMinutes} minuten`;
}